import { inject, Injectable } from '@angular/core';
import {
  OryCreateInput,
  OryCreateOutput,
  OryGetInput,
  OryGetOutput,
  OryExecuteInput,
  OryExecuteOutput,
} from './model';
import type { FlowError } from '@ory/client';
import { Observable, switchMap } from 'rxjs';
import {
  OryLoginFlow,
  OryLogoutFlow,
  OryVerificationFlow,
  OryRecoveryFlow,
  OryRegistrationFlow,
  OrySettingsFlow,
  OrySessionFlow,
  OryErrorFlow,
} from './flows';
import type { OryKratosFlowWithUIType } from './model';

type GetFlowHandlerMap<IdentityTraits extends object> = {
  [FlowType in OryKratosFlowWithUIType]: (input: OryGetInput<FlowType, IdentityTraits>) =>
    Observable<OryGetOutput<FlowType, IdentityTraits>>;
};

type CreateFlowHandlerMap<IdentityTraits extends object = object> = {
  [FlowType in OryKratosFlowWithUIType]: (
    input?: OryCreateInput<FlowType, IdentityTraits>,
  ) => Observable<OryCreateOutput<FlowType, IdentityTraits>>;
};

type ExecuteFlowHandlerMap<IdentityTraits extends object = object> = {
  [FlowType in OryKratosFlowWithUIType]: (
    flow: OryGetOutput<FlowType, IdentityTraits>,
    input: OryExecuteInput<FlowType, IdentityTraits>
  ) => Observable<OryExecuteOutput<FlowType, IdentityTraits>>;
};

/**
 * Main service for interacting with Ory Kratos self-service flows.
 *
 * Provides unified, type-safe access to Kratos flow operations through generic
 * methods that delegate to specialized flow handlers. All flows follow the
 * consistent create → get → execute pattern.
 *
 * @remarks
 * For OAuth2/Hydra flows, use {@link OryOAuthService} instead.
 * This service focuses on Kratos self-service flows (login, registration,
 * recovery, settings, verification, logout).
 */
@Injectable({
  providedIn: 'root'
})
export class OryService<IdentityTraits extends object> {

  readonly #sessionFlow = inject(OrySessionFlow<IdentityTraits>);
  readonly #loginFlow = inject(OryLoginFlow<IdentityTraits>);
  readonly #registrationFlow = inject(OryRegistrationFlow<IdentityTraits>);
  readonly #recoveryFlow = inject(OryRecoveryFlow<IdentityTraits>);
  readonly #settingsFlow = inject(OrySettingsFlow<IdentityTraits>);
  readonly #errorFlow = inject(OryErrorFlow);
  readonly #verificationFlow = inject(OryVerificationFlow);
  readonly #logoutFlow = inject(OryLogoutFlow);

  #getFlowHandler: GetFlowHandlerMap<IdentityTraits> = {
    login: input => this.#loginFlow.get(input),
    registration: input => this.#registrationFlow.get(input),
    recovery: input => this.#recoveryFlow.get(input),
    settings: input => this.#settingsFlow.get(input),
    verification: input => this.#verificationFlow.get(input),
  };

  #createFlowHandler: CreateFlowHandlerMap<IdentityTraits> = {
    login: input => this.#loginFlow.create(input),
    registration: input => this.#registrationFlow.create(input),
    recovery: input => this.#recoveryFlow.create(input),
    settings: input => this.#settingsFlow.create(input),
    verification: input => this.#verificationFlow.create(input),
  };

  #executeFlowHandler: ExecuteFlowHandlerMap<IdentityTraits> = {
    login: (flow, input) => this.#loginFlow.execute(flow, input),
    registration: (flow, input) => this.#registrationFlow.execute(flow, input),
    recovery: (flow, input) => this.#recoveryFlow.execute(flow, input),
    settings: (flow, input) => this.#settingsFlow.execute(flow, input),
    verification: (flow, input) => this.#verificationFlow.execute(flow, input),
  };

  // ============================================================================
  // Explicit Accessors (Session & Error)
  // ============================================================================

  /**
   * Retrieves the current user session.
   *
   * @param input - Optional session request parameters (tokenize_as)
   * @returns Observable of the session object
   *
   * @throws OryServiceError - 401 if no active session, or API error
   */
  getSession(input: OryGetInput<'session', IdentityTraits> = {}): Observable<OryGetOutput<'session', IdentityTraits>> {
    return this.#sessionFlow.get(input);
  }

  /**
   * Retrieves error details by error ID.
   *
   * Used when Ory redirects to error page with `?id=<error-id>`.
   *
   * @param input - Error request with ID
   * @returns Observable of the flow error details
   */
  getError(input: OryGetInput<'error'>): Observable<FlowError> {
    return this.#errorFlow.get(input);
  }

  // ============================================================================
  // Explicit Logout Methods
  // ============================================================================

  /**
   * Creates a logout flow to obtain logout token and URL.
   *
   * @remarks
   * Use this when you need to show a confirmation UI before logging out.
   * The returned flow contains `logout_token` and `logout_url`.
   *
   * @param input - Optional logout flow configuration
   * @returns Observable of logout flow with token and URL
   */
  createLogout(input: OryCreateInput<'logout'> = {}): Observable<OryCreateOutput<'logout'>> {
    return this.#logoutFlow.create(input);
  }

  /**
   * Executes a logout flow to invalidate the session.
   *
   * @param flow - Logout flow from createLogout()
   * @returns Observable that completes when logout is successful
   */
  executeLogout(flow: OryCreateOutput<'logout'>): Observable<void> {
    return this.#logoutFlow.execute(flow);
  }

  /**
   * Convenience method: creates and executes logout in one step.
   *
   * @remarks
   * Use this for immediate logout without confirmation UI.
   * Internally calls createLogout() then executeLogout().
   *
   * @param input - Optional logout flow configuration
   * @returns Observable that completes when logout is successful
   */
  logout(input: OryCreateInput<'logout'> = {}): Observable<void> {
    return this.#logoutFlow.create(input).pipe(
      switchMap(flow => this.#logoutFlow.execute(flow))
    );
  }

  // ============================================================================
  // Generic Flow Methods (Flows with UI)
  // ============================================================================

  /**
   * Retrieves an existing flow by ID.
   *
   * @remarks
   * Delegates to specialized flow handlers while maintaining type safety.
   * Flow must have been previously created.
   *
   * For session/error retrieval, prefer {@link getSession} and {@link getError}.
   *
   * @param flowType - Type of flow to retrieve
   * @param input - Flow identifier
   * @returns Observable of the retrieved flow object
   *
   * @throws OryServiceError - Flow not found or API error
   */
  getFlow<const FlowType extends OryKratosFlowWithUIType>(
    flowType: FlowType,
    input: OryGetInput<FlowType, IdentityTraits>,
  ): Observable<OryGetOutput<FlowType, IdentityTraits>> {
    return this.#getFlowHandler[flowType](input);
  }

  /**
   * Creates a new flow instance for client-initiated flows.
   *
   * @remarks
   * Delegates to specialized flow handlers while maintaining type safety.
   * Only available for flows that support client-initiated creation.
   *
   * @param flowType - Type of flow to create
   * @param input - Optional configuration (return_to, login_challenge, etc.)
   * @returns Observable of the created flow object
   *
   * @throws OryServiceError - Failed to create flow
   */
  createFlow<const FlowType extends OryKratosFlowWithUIType>(
    flowType: FlowType,
    input?: OryCreateInput<FlowType, IdentityTraits>,
  ): Observable<OryCreateOutput<FlowType, IdentityTraits>> {
    return this.#createFlowHandler[flowType](input);
  }

  /**
   * Executes (submits) a flow with user input or action data.
   *
   * @remarks
   * Delegates to specialized flow handlers while maintaining type safety.
   * Automatically includes CSRF token and validates flow state. Flow object
   * must contain current flow state from create or get operations.
   *
   * @param flowType - Type of flow to execute
   * @param flow - Current flow object with id and state
   * @param input - User input or action data to submit
   * @returns Observable of execution result (may include session, redirect, or updated flow)
   *
   * @throws OryBrowserRedirectError - Browser redirect required (flow expired/replaced)
   * @throws OryFlowValidationError - Validation failed
   * @throws OryServiceError - Network or API error
   */
  executeFlow<const FlowType extends OryKratosFlowWithUIType>(
    flowType: FlowType,
    flow: OryGetOutput<FlowType, IdentityTraits>,
    input: OryExecuteInput<FlowType, IdentityTraits>
  ): Observable<OryExecuteOutput<FlowType, IdentityTraits>> {
    return this.#executeFlowHandler[flowType](flow, input);
  }
}
